/**
 * WildlifeDetectionResults
 *
 * Design: Nature/Wildlife dashboard
 * - Immersive hero stats
 * - Species distribution with distinct styling
 * - Individual detection cards with high visibility confidence scores
 */
import React from "react";
import { WildlifeDetectionResults as ResultsType } from "../../types";
import { formatNumber } from "../../utils";
import {
  Search,
  PawPrint,
  TreePine,
  Target,
  Camera,
  Maximize,
} from "lucide-react";

interface WildlifeDetectionResultsProps {
  results: ResultsType;
}

export const WildlifeDetectionResults: React.FC<
  WildlifeDetectionResultsProps
> = ({ results }) => {
  const isVideo = results.type === "video";
  const totalAnimals = isVideo
    ? results.total_animals_detected || 0
    : results.animals_detected || 0;

  // Helper to get animal emoji/icon
  const getSpeciesIcon = (species: string) => {
    const s = species.toLowerCase();
    if (s.includes("panda")) return "🐼";
    if (s.includes("elephant")) return "🐘";
    if (s.includes("tiger")) return "🐯";
    if (s.includes("lion")) return "🦁";
    if (s.includes("bear")) return "🐻";
    if (s.includes("monkey")) return "🐵";
    if (s.includes("deer")) return "🦌";
    if (s.includes("bird")) return "🦅";
    if (s.includes("wolf") || s.includes("fox")) return "🐺";
    return "🐾";
  };

  return (
    <div className="space-y-6">
      {/* Hero Stats */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600/20 via-slate-800/50 to-slate-900 border border-emerald-500/30 p-8">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl opacity-60" />
        <div className="absolute -bottom-8 -right-8 opacity-10 rotate-12">
          <TreePine className="w-48 h-48 text-emerald-400" />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <PawPrint className="w-10 h-10 text-white" />
            </div>
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1">
                Wildlife Monitoring
              </p>
              <h2 className="text-5xl font-bold text-white tracking-tight">
                {formatNumber(totalAnimals)}
              </h2>
              <p className="text-emerald-400 text-sm mt-2 flex items-center gap-2">
                <Search className="w-4 h-4" />
                {isVideo
                  ? `${results.unique_species_count} species identified`
                  : "Total detections"}
              </p>
            </div>
          </div>

          {isVideo && (
            <div className="flex gap-4">
              <div className="bg-slate-900/50 backdrop-blur-md rounded-xl p-4 border border-slate-700/50 text-center min-w-[140px]">
                <p className="text-2xl font-bold text-white mb-1">
                  {results.average_animals_per_frame?.toFixed(1) || 0}
                </p>
                <p className="text-slate-400 text-xs">Avg per Frame</p>
              </div>
              <div className="bg-slate-900/50 backdrop-blur-md rounded-xl p-4 border border-slate-700/50 text-center min-w-[140px]">
                <p className="text-2xl font-bold text-emerald-400 mb-1">
                  {results.max_animals_in_single_frame || 0}
                </p>
                <p className="text-white text-xs opacity-80">Max Concurrent</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Species Distribution */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <Target className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Species Breakdown</h3>
            <p className="text-slate-400 text-sm">Identified animals</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(results.species_detected)
            .sort(([, a], [, b]) => b - a)
            .map(([species, count]) => (
              <div
                key={species}
                className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="text-3xl bg-slate-800 rounded-lg w-12 h-12 flex items-center justify-center shadow-inner">
                    {getSpeciesIcon(species)}
                  </div>
                  <div>
                    <h4 className="text-white font-medium capitalize text-lg">
                      {species}
                    </h4>
                    <span className="text-slate-500 text-xs">Species</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-emerald-400">
                    {formatNumber(count)}
                  </p>
                  <p className="text-slate-500 text-xs">detected</p>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Individual Detections (Grid) */}
      {results.detections && results.detections.length > 0 && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Camera className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Recent Detections</h3>
              <p className="text-slate-400 text-sm">Detailed logs</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {results.detections.slice(0, 12).map((detection, idx) => (
              <div
                key={idx}
                className="bg-slate-900/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-primary-500/30 transition-colors"
              >
                {/* Header */}
                <div className="px-4 py-3 border-b border-slate-700/30 flex items-center justify-between bg-slate-800/30">
                  <span className="text-lg">
                    {getSpeciesIcon(detection.species)}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full border ${
                      detection.confidence > 0.8
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                        : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                    }`}
                  >
                    {(detection.confidence * 100).toFixed(0)}% Conf
                  </span>
                </div>
                {/* Body */}
                <div className="p-4 space-y-2">
                  <h4 className="text-white font-medium capitalize flex items-center justify-between">
                    {detection.species}
                  </h4>

                  <div className="flex items-center justify-between text-xs text-slate-400 mt-2">
                    <div className="flex items-center gap-1.5 bg-slate-800 px-2 py-1 rounded">
                      <Maximize className="w-3 h-3" />
                      <span className="capitalize">
                        {detection.size_category || "Unknown Size"}
                      </span>
                    </div>
                  </div>

                  <div className="pt-2 mt-2 border-t border-slate-700/30">
                    <p className="text-[10px] text-slate-500 font-mono truncate">
                      BBox: [{detection.bbox.map((n) => n.toFixed(0)).join(",")}
                      ]
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
